import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import I18n, { translate } from '@utils/i18n';
import { Dialog } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';

const namespace = 'features.buildings.price_list_selector';

const accountInput = document.querySelector('[name*="building[account_id]"');

function PriceListSelector({ priceList, isNewRecord, accountsPriceLists, priceLists, associatedItems }) {
  const [priceListId, setPriceListId] = useState(priceList.id);
  const [openAsociated, setOpenAsociated] = useState(false);
  const [openAccount, setOpenAccount] = useState(false);
  const [foundPriceListId, setFoundPriceListId] = useState('');
  const [filterQuery, setFilterQuery] = useState('');

  const ddListValue = () => {
    if (priceListId === undefined || priceListId === null || priceListId === '') return null;

    // A building can reference a price list that is inactive, and `priceLists` only carries
    // active ones — so a valid id can still be absent here.
    const selected = priceLists.find((elem) => elem.value === priceListId);
    if (!selected) return null;

    return {
      value: priceListId,
      label: selected.label
    };
  };

  const onFilterChange = (event) => {
    setFilterQuery(event.filter.value);
  };

  const filteredPriceLists = useMemo(() => {
    if (filterQuery === '') return priceLists;

    const re = new RegExp(filterQuery, 'i');

    return priceLists.filter((list) => re.test(list.label));
  }, [filterQuery]);

  function handleAccountChange() {
    if (this.value === '') {
      setPriceListId(priceList.id);
      return;
    }

    const account = accountsPriceLists.find((item) => item.id.toString() === this.value);

    setOpenAccount(true);
    setFoundPriceListId(account?.price_list_id ?? null);
  }

  useEffect(() => {
    if (!isNewRecord || !accountInput) return;

    accountInput.addEventListener('change', handleAccountChange);

    return () => {
      accountInput.removeEventListener('change', handleAccountChange);
    };
  }, [priceListId]);

  const onChange = (e) => {
    const newId = e.target.value.value;
    setPriceListId(newId);

    if (isNewRecord) return;

    if (priceList.id !== newId && Object.values(associatedItems).some((count) => count > 0)) setOpenAsociated(true);
  };

  const onCloseAssociatedModal = () => {
    setPriceListId(priceList.id);
    setOpenAsociated(false);
  };

  return (
    <>
      <div className="qmb-select" style={{ minWidth: '200px' }}>
        <label className="qmb-label">{translate('label', { namespace })}</label>
        <DropDownList
          required
          filterable
          onFilterChange={onFilterChange}
          id="priceListSelector"
          className="qmb-popup"
          textField="label"
          dataItemKey="value"
          data={filteredPriceLists}
          value={ddListValue()}
          onChange={onChange}
        />
      </div>
      {openAsociated && (
        <Dialog
          title=<h4>{translate('dialog_title', { namespace })}</h4>
          className="qmb-dialog"
          width={600}
          onClose={onCloseAssociatedModal}>
          <p>{translate('note', { namespace })}</p>

          <br />

          {associatedItems &&
            Object.entries(associatedItems).map(([key, value]) => {
              if (value > 0) {
                return (
                  <p key={key}>
                    {value} {translate(key, { namespace })}
                  </p>
                );
              }
              return null;
            })}

          <br />

          {Object.values(associatedItems).some((count) => count > 0) && (
            <p style={{ fontStyle: 'italic', fontSize: '0.9em' }}>
              {translate('sub_note_for_pending_items', { namespace })}
            </p>
          )}

          <div className="modal__footer pull-right" style={{ justifyContent: 'space-between' }}>
            <button type="button" className="qmb-button" onClick={onCloseAssociatedModal}>
              {translate('cancel', { namespace: 'generic' })}
            </button>
            <button
              onClick={() => {
                setOpenAsociated(false);
              }}
              type="submit"
              className="qmb-button--submit">
              {translate('confirm', { namespace: 'generic' })}
            </button>
          </div>
        </Dialog>
      )}

      {openAccount && (
        <Dialog
          title={translate('account_title', { namespace })}
          className="qmb-dialog"
          onClose={() => {
            setOpenAccount(false);
          }}>
          <div className="modal__footer pull-right" style={{ justifyContent: 'space-between' }}>
            <button
              type="button"
              className="qmb-button"
              onClick={() => {
                setOpenAccount(false);
              }}>
              {I18n.t('generic.cancel')}
            </button>
            <button
              onClick={() => {
                setPriceListId(foundPriceListId);
                setOpenAccount(false);
              }}
              type="submit"
              className="qmb-button--submit">
              {I18n.t('generic.confirm')}
            </button>
          </div>
        </Dialog>
      )}
      <input type="hidden" name="building[price_list_id]" value={priceListId} id="building_price_list_id" />
    </>
  );
}

PriceListSelector.propTypes = {
  priceList: PropTypes.object.isRequired,
  priceLists: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string
    })
  ).isRequired,
  isNewRecord: PropTypes.bool,
  accountsPriceLists: PropTypes.array.isRequired,
  associatedItems: PropTypes.shape({
    proposals: PropTypes.number,
    work_orders: PropTypes.number,
    inspections: PropTypes.number,
    invoices: PropTypes.number
  }).isRequired
};

PriceListSelector.defaultProps = {
  isNewRecord: false
};

export default PriceListSelector;
